import * as React from "react";

import { Box, Link, makeStyles, Theme, Typography } from "@material-ui/core";

import socialProofBeautyChef from "client/assets/social-proof-beauty-chef.jpg";
import socialProofBeginningBoutique from "client/assets/social-proof-beginning-boutique.jpg";
import { useBranding } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";

const useStyles = makeStyles((theme: Theme) => {
  const { gray90 } = useNormalizedBrandPaletteValues(theme);

  return {
    socialProofHeader: {
      color: theme.palette.common.white,
      fontSize: `${24 / 16}rem`,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(8),
      marginLeft: theme.spacing(10),
      whiteSpace: "pre-line",
    },
    socialProofLink: {
      color: theme.palette.primary.main,
      fontSize: `${14 / 16}rem`,
      lineHeight: `${22 / 16}rem`,
    },
    socialProofQuote: {
      color: theme.palette.common.white,
      fontSize: `${20 / 16}rem`,
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: `${28 / 16}rem`,
      marginBottom: theme.spacing(1),
      whiteSpace: "pre-line",
    },
    socialProofDescription: {
      color: gray90,
      fontSize: `1rem`,
      lineHeight: `${24 / 16}rem`,
      marginBottom: theme.spacing(1),
      whiteSpace: "pre-line",
    },
    socialProofWrapper: {
      backgroundColor: theme.palette.common.black,
    },
    socialProofCardImage: {
      backgroundColor: theme.palette.common.white,
      borderRadius: "50%",
      height: "64px",
      width: "64px",
      marginLeft: theme.spacing(10),
      marginRight: theme.spacing(2.5),
    },
  };
});

type SocialProofProps = {
  description: string;
  headShotUrl: string;
  linkUrl: string;
  quote: string;
};

const SocialProofHeader: React.FC = () => {
  const classes = useStyles();
  const { brandTitle } = useBranding();
  return (
    <Box>
      <Typography className={classes.socialProofHeader}>
        {`How ${brandTitle} helps`}
      </Typography>
    </Box>
  );
};

const SocialProof: React.FC<SocialProofProps> = ({
  description,
  headShotUrl,
  linkUrl,
  quote,
}) => {
  const classes = useStyles();

  return (
    <Box
      display="flex"
      width="100%"
      height="100%"
      flexDirection="row"
      alignItems="flex-start"
      flexGrow={1}
      mb={12}
    >
      <img
        alt="black logo"
        className={classes.socialProofCardImage}
        src={headShotUrl}
      />
      <Box display="flex" flexDirection="column" mr={12}>
        <Typography className={classes.socialProofQuote}>{quote}</Typography>
        <Typography className={classes.socialProofDescription}>
          &mdash; {description}
        </Typography>
        <Link
          className={classes.socialProofLink}
          color="primary"
          href={linkUrl}
          rel="noopener"
          target="_blank"
        >
          Read more
        </Link>
      </Box>
    </Box>
  );
};

export const SocialProofs: React.FC = () => {
  const classes = useStyles();

  const socialProofObjs: SocialProofProps[] = [
    {
      description: `Sarah, Beginning Boutique`,
      headShotUrl: socialProofBeginningBoutique,
      linkUrl: `https://www.afterpay.com/en-AU/business/access/success-stories/beginning-boutique-sarah-timmerman-afterpay-day-sale`,
      quote: `"Our sales soared by 500%"`,
    },
    {
      description: `Laura, The Beauty Chef`,
      headShotUrl: socialProofBeautyChef,
      linkUrl: `https://www.afterpay.com/en-AU/business/access/growing-with-afterpay/how-afterpay-day-sale-helped-the-beauty-chef-reached`,
      quote: `"We saw a 380% increase"`,
    },
  ];

  return (
    <Box
      alignItems="center"
      className={classes.socialProofWrapper}
      display="flex"
      flexDirection="column"
      height="100%"
      justifyContent="center"
    >
      <Box
        alignItems="flex-start"
        flexDirection="column"
        height="100%"
        pt={20}
        width="100%"
      >
        <SocialProofHeader />
        {socialProofObjs.map((socialProofObj, index) => (
          <Box key={`social_proof_${index}`}>
            <SocialProof {...socialProofObj} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
